import { useState, useMemo } from 'react';

import PropTypes from 'prop-types';
import { BsSearch, BsPlus, BsCheck2 } from 'react-icons/bs';

import TextInput from '#globalComponents/Forms/TextInput';

const MAX_RESULTS = 25;

/**
 * Client-side fuzzy search across all WZ nodes by name, code prefix, or keywords.
 * Clicking a result toggles its selection in the shared selectedSet.
 */
export const WzCodeAutocomplete = ({ nodes, selectedSet, onToggle }) => {
	const [query, setQuery] = useState('');

	const results = useMemo(() => {
		const q = query.trim().toLowerCase();
		if (q.length < 2) return [];

		const matches = [];
		for (const n of nodes) {
			if (n.level === 1) continue; // Skip broad section headers from autocomplete
			const nameMatch = n.name.toLowerCase().includes(q);
			const codeMatch = n.code.toLowerCase().startsWith(q);
			const keywordMatch =
				!nameMatch &&
				!codeMatch &&
				Array.isArray(n.keywords) &&
				n.keywords.some((k) => k.toLowerCase().includes(q));

			if (nameMatch || codeMatch || keywordMatch) {
				matches.push(n);
				if (matches.length >= MAX_RESULTS) break;
			}
		}
		return matches;
	}, [query, nodes]);

	return (
		<div className="space-y-2">
			<div className="relative">
				<TextInput
					value={query}
					onChange={(e) => setQuery(e.target.value)}
					placeholder="Branche suchen (z.B. Sanitär, 28.41, Maschinenbau)…"
					className="pr-9"
				/>
				<BsSearch className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-bgBase-800 pointer-events-none" />
			</div>

			{query.trim().length >= 2 && (
				<div className="border border-gray-200 rounded-md max-h-48 overflow-y-auto bg-white shadow-sm">
					{results.length === 0 ? (
						<div className="px-3 py-2 text-xs text-bgBase-800 text-center">Keine Treffer</div>
					) : (
						results.map((n) => {
							const isSelected = selectedSet.has(n.code);
							return (
								<button
									key={n.code}
									type="button"
									onClick={() => onToggle(n, !isSelected)}
									className={`w-full text-left px-3 py-1.5 text-xs flex items-center gap-2 transition-colors border-l-2 ${
										isSelected
											? 'bg-purple-50 text-primary-700 border-purple-500 font-medium'
											: 'border-transparent hover:bg-purple-50/50 hover:border-purple-300 text-text'
									}`}
								>
									{isSelected ? (
										<BsCheck2 className="w-3.5 h-3.5 text-primary-600 flex-shrink-0" />
									) : (
										<BsPlus className="w-3.5 h-3.5 text-bgBase-800 flex-shrink-0" />
									)}
									<span className="font-mono text-[10px] text-bgBase-800 flex-shrink-0 w-14">
										{n.code}
									</span>
									<span className="truncate">{n.name}</span>
									<span className="ml-auto text-[10px] text-bgBase-800 flex-shrink-0">
										L{n.level}
									</span>
								</button>
							);
						})
					)}
				</div>
			)}
		</div>
	);
};

WzCodeAutocomplete.propTypes = {
	nodes: PropTypes.array.isRequired,
	selectedSet: PropTypes.instanceOf(Set).isRequired,
	onToggle: PropTypes.func.isRequired,
};

export default WzCodeAutocomplete;
